<script setup lang="ts">
import { computed } from "vue";
import type {
  DashboardNavExternalLink,
  DashboardNavGroup,
  DashboardNavItem,
  DashboardNavLink,
} from "../../features/dashboard/dashboard.types.ts";
import LucideIcon from "../icons/LucideIcon.vue";
import DashboardSidebarLink from "./DashboardSidebarLink.vue";

// Recursive so a group's (or a link's) children can themselves be a link with
// its own children, without duplicating this rendering logic per nesting level.
const props = defineProps<{
  item: DashboardNavItem;
  indented?: boolean;
  isActive: (item: DashboardNavLink | DashboardNavExternalLink) => boolean;
  isGroupExpanded: (item: { key: string }) => boolean;
  toggleGroup: (item: { key: string }) => void;
  visibleChildren: (
    item: DashboardNavGroup | DashboardNavLink
  ) => DashboardNavLink[];
}>();

// A template-local `as X | Y` cast reads as deprecated Vue filter syntax to
// the linter, so the leaf-node narrowing (external link, or a childless
// route) happens here instead.
const leafItem = computed(
  () => props.item as DashboardNavLink | DashboardNavExternalLink
);
</script>

<template>
  <div
    v-if="item.kind === 'group'"
    class="grid gap-2"
    :class="indented ? 'ps-6' : 'ps-3'"
  >
    <button
      type="button"
      class="dashboard-sidebar-group-toggle flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors"
      :aria-expanded="isGroupExpanded(item)"
      :aria-controls="`dashboard-nav-group-${item.key}`"
      @click="toggleGroup(item)"
    >
      <span class="text-lg leading-none">{{ item.icon }}</span>
      <span class="min-w-0 flex-1 text-sm font-semibold">
        {{ item.label }}
      </span>
      <LucideIcon
        name="chevron-down"
        :size="20"
        class="dashboard-sidebar-group-chevron"
        :class="{
          'dashboard-sidebar-group-chevron--open': isGroupExpanded(item),
        }"
      />
    </button>

    <!-- Kept mounted so the open/close can animate; `inert` keeps the
         collapsed children out of the tab order and a11y tree. -->
    <div
      :id="`dashboard-nav-group-${item.key}`"
      class="dashboard-nav-group-panel"
      :class="{
        'dashboard-nav-group-panel--open': isGroupExpanded(item),
      }"
      :inert="isGroupExpanded(item) ? undefined : true"
    >
      <div class="dashboard-nav-group-panel-inner grid gap-2">
        <DashboardSidebarNavItem
          v-for="child in item.children"
          :key="child.key"
          :item="child"
          indented
          :is-active="isActive"
          :is-group-expanded="isGroupExpanded"
          :toggle-group="toggleGroup"
          :visible-children="visibleChildren"
        />
      </div>
    </div>
  </div>

  <div
    v-else-if="
      item.kind === 'route' && item.children && item.children.length > 0
    "
    class="grid gap-2"
  >
    <div class="flex items-stretch gap-1">
      <DashboardSidebarLink
        :item="item"
        :is-collapsed="false"
        :is-active="isActive(item)"
        :indented="indented"
        class="flex-1"
      />
      <button
        v-if="visibleChildren(item).length > 0"
        type="button"
        class="dashboard-sidebar-group-toggle flex w-9 shrink-0 items-center justify-center rounded-xl border"
        :aria-expanded="isGroupExpanded(item)"
        :aria-controls="`dashboard-nav-children-${item.key}`"
        :aria-label="`Toggle ${item.label} sub-navigation`"
        @click="toggleGroup(item)"
      >
        <LucideIcon
          name="chevron-down"
          :size="18"
          class="dashboard-sidebar-group-chevron"
          :class="{
            'dashboard-sidebar-group-chevron--open': isGroupExpanded(item),
          }"
        />
      </button>
    </div>

    <div
      v-if="visibleChildren(item).length > 0"
      :id="`dashboard-nav-children-${item.key}`"
      class="dashboard-nav-group-panel"
      :class="{
        'dashboard-nav-group-panel--open': isGroupExpanded(item),
      }"
      :inert="isGroupExpanded(item) ? undefined : true"
    >
      <div class="dashboard-nav-group-panel-inner grid gap-2">
        <DashboardSidebarNavItem
          v-for="child in visibleChildren(item)"
          :key="child.key"
          :item="child"
          indented
          :is-active="isActive"
          :is-group-expanded="isGroupExpanded"
          :toggle-group="toggleGroup"
          :visible-children="visibleChildren"
        />
      </div>
    </div>
  </div>

  <DashboardSidebarLink
    v-else
    :item="leafItem"
    :is-collapsed="false"
    :is-active="isActive(leafItem)"
    :indented="indented"
  />
</template>
